import { isNewBuildAvailable, UNVERSIONED_BUILD } from "../../shared/build-version";

/**
 * "새 빌드가 나갔나"를 브라우저에서 묻는 한 곳.
 *
 * 내 버전은 빌드 때 번들에 구워진 VITE_APP_VERSION(Dockerfile.web 이 APP_VERSION 을 그대로 넘긴다),
 * 서버 버전은 nginx 가 no-store 로 내주는 /version.txt(deploy/web/snippets/version.conf).
 * 판정 규칙은 shared/build-version.ts — 로컬(dev)에서는 절대 true 가 안 나온다.
 *
 * 쓰는 곳:
 *  - NewBuildContext: 1분마다·탭 복귀·화면 이동 때 물어 모달(NewBuildModal)·헤더 버튼(NewBuildHeaderButton)을 띄운다.
 *  - ErrorBoundary: 렌더 에러가 났을 때 물어, 새 빌드면 에러 화면 대신 조용히 한 번 새로고침한다.
 */
export const CURRENT_BUILD_VERSION: string =
  (import.meta.env.VITE_APP_VERSION as string | undefined)?.trim() || UNVERSIONED_BUILD;

/** 서버가 지금 내주는 화면 버전. 못 읽으면 null(오프라인·차단 등) — 호출부는 "모름"으로 다룬다. */
export async function fetchServedBuildVersion(timeoutMs = 3000): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`/version.txt?t=${Date.now()}`, {
      cache: "no-store",
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    // SPA 폴백이 삼켜 HTML 이 돌아오는 경우(스니펫이 빠진 주소)를 버전으로 오인하지 않게 막는다.
    if (!text || text.length > 80 || text.startsWith("<")) return null;
    return text;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** 새 빌드가 나갔으면 true. 판단 재료가 없으면(dev·네트워크 실패) false. */
export async function hasNewBuild(timeoutMs?: number): Promise<boolean> {
  if (CURRENT_BUILD_VERSION === UNVERSIONED_BUILD) return false;
  const served = await fetchServedBuildVersion(timeoutMs);
  return isNewBuildAvailable(CURRENT_BUILD_VERSION, served);
}
